import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  Sparkles,
  FlaskConical,
  Mic,
  CalendarDays,
  RefreshCw,
  CheckCircle2,
  Copy,
  ExternalLink,
  Lock,
  Swords,
  X,
  Info,
  ChevronDown
} from 'lucide-react';
import { GlobalNav } from '../components/GlobalNav';
import GlobalFooter from '../components/GlobalFooter';
import { SPARK_CATEGORIES, getRandomSparkPrompt, getDailyDrop } from '../lib/arcade/sparkPrompts';
import { addSparkHistoryItem, loadSparkHistory } from '../lib/arcade/storage';
import apiClient from '../api/client';
import { AuthContext } from '../App';
import { generateSparkShareCard } from '../lib/arcade/shareCard';

const TABS = [
  { key: 'spark', label: 'Spark', icon: Sparkles },
  { key: 'chemistry', label: 'Chemistry', icon: FlaskConical },
  { key: 'voice', label: 'Voice', icon: Mic },
  { key: 'microdate', label: 'Micro-Date', icon: CalendarDays },
];

export default function ConnectionArcadePage() {
  const { user } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState('spark');
  const [sparkType, setSparkType] = useState('any'); // any | signal | spark
  const [currentPrompt, setCurrentPrompt] = useState(null);
  const [answer, setAnswer] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [history, setHistory] = useState([]);
  const [showHowItWorks, setShowHowItWorks] = useState(false);

  // Step 3: Invite link state
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteUrl, setInviteUrl] = useState('');
  const [inviteExpiresAt, setInviteExpiresAt] = useState('');
  const [inviteError, setInviteError] = useState('');
  const [inviteCopied, setInviteCopied] = useState(false);
  const [inviteFilterMode, setInviteFilterMode] = useState('standard'); // standard | expanded

  // Step 6: Sender "My Invites" inbox
  const [myInvitesLoading, setMyInvitesLoading] = useState(false);
  const [myInvitesError, setMyInvitesError] = useState('');
  const [myInvites, setMyInvites] = useState([]);

  // Share Card state
  const [shareOpen, setShareOpen] = useState(false);
  const [shareIncludeAnswer, setShareIncludeAnswer] = useState(false);
  const [shareBusy, setShareBusy] = useState(false);
  const [shareError, setShareError] = useState('');
  const [shareInviteToken, setShareInviteToken] = useState('');
  const [shareInviteUrl, setShareInviteUrl] = useState('');

  // --- Smart Layer: highlight Constellation for open/poly profiles ---
  const isExpansiveAligned = (() => {
    const v = (x) => (typeof x === "string" ? x.toLowerCase() : "");
    const candidates = [
      user?.relationship_style,
      user?.relationshipStyle,
      user?.profile?.relationship_style,
      user?.profile?.relationshipStyle,
      user?.discover_preferences?.relationship_style,
      user?.discover_preferences?.relationshipStyle,
      user?.discoverPreferences?.relationship_style,
      user?.discoverPreferences?.relationshipStyle,
    ].map(v).filter(Boolean);
    const raw = candidates.join(" ");
    return (
      raw.includes("poly") ||
      raw.includes("open") ||
      raw.includes("nonmonog") ||
      raw.includes("non-monog") ||
      raw.includes("enm") ||
      raw.includes("swing")
    );
  })();

  const ActiveIcon = useMemo(() => {
    return TABS.find(t => t.key === activeTab)?.icon || Sparkles;
  }, [activeTab]);

  const currentCategoryLabel = SPARK_CATEGORIES?.[sparkType]?.label || "Spark";

  const shareUrl = (() => {
    const rc = user?.referral_code || user?.referralCode || user?.profile?.referral_code || user?.profile?.referralCode || "";
    const base = `${window.location.origin}/arcade`;
    return rc ? `${base}?ref=${encodeURIComponent(rc)}` : base;
  })();

  async function ensureShareInviteUrl() {
    if (shareInviteUrl && shareInviteToken) return { token: shareInviteToken, invite_url: shareInviteUrl };

    if (!currentPrompt) throw new Error("Missing prompt");
    const trimmed = (answer || '').trim();
    if (!trimmed) throw new Error("Missing answer");

    const res = await apiClient.post('/arcade/invite/create', {
      prompt_id: currentPrompt.id,
      prompt_text: currentPrompt.text,
      initiator_answer: trimmed,
      ttl_hours: 72,
      filter_mode: inviteFilterMode,
    });

    const token = res?.data?.token || '';
    const invite_url = res?.data?.invite_url || '';
    const invite_share_url = res?.data?.invite_share_url || '';
    const invite_friendly_url = res?.data?.invite_friendly_url || '';
    if (!token || (!invite_share_url && !invite_url)) throw new Error("Invite link not returned");

    // For share text: use the short friendly URL (joinunitylove.com/spark/Unity-ABC123)
    // For OG crawlers: use the share_url (has OG tags + redirect)
    let share_url = invite_friendly_url || invite_share_url;
    if (!share_url && invite_url) {
      try {
        const u = new URL(invite_url);
        const ref = u.searchParams.get('ref') || '';
        share_url = `${u.origin}/api/arcade/invite/${token}/share` + (ref ? `?ref=${encodeURIComponent(ref)}` : '');
      } catch {
        share_url = invite_url;
      }
    }

    setShareInviteToken(token);
    setShareInviteUrl(share_url || invite_url);
    fetchMyInvites();
    return { token, invite_url: (share_url || invite_url) };
  }

  async function handleShareCard() {
    setShareBusy(true);
    setShareError('');
    try {
      const { invite_url } = await ensureShareInviteUrl();

      const blob = await generateSparkShareCard({
        categoryLabel: currentCategoryLabel,
        prompt: currentPrompt?.text || '',
        answer: answer || '',
        includeAnswer: shareIncludeAnswer,
      });
      if (!blob) throw new Error("No image generated.");

      const file = new File([blob], "unitylove-spark.png", { type: "image/png" });
      const text = `Play this Spark with me in UnityLOVE \u2192 ${invite_url}`;

      if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], text });
      } else if (navigator.share) {
        try { await navigator.share({ text }); } catch { downloadBlob(blob); }
      } else {
        downloadBlob(blob);
      }
      setShareOpen(false);
    } catch (e) {
      setShareError("Could not create the share link/card. Try again.");
    } finally {
      setShareBusy(false);
    }
  }

  function downloadBlob(blob) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "unitylove-spark.png";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  // Initialize Spark Solo state on first visit + whenever returning to Spark tab
  useEffect(() => {
    if (activeTab !== 'spark') return;
    setHistory(loadSparkHistory());
    fetchArcadeHistory();
    setSubmitted(false);
    setAnswer('');
    setCurrentPrompt(sparkType === 'daily' ? getDailyDrop() : getRandomSparkPrompt(sparkType));
    setInviteUrl('');
    setInviteExpiresAt('');
    setInviteError('');
    setInviteFilterMode('standard');
    fetchMyInvites();
    // eslint-disable-next-line
  }, [activeTab]);

  // When user changes type, refresh prompt (Spark tab only)
  useEffect(() => {
    if (activeTab !== 'spark') return;
    setSubmitted(false);
    setAnswer('');
    setCurrentPrompt(sparkType === 'daily' ? getDailyDrop() : getRandomSparkPrompt(sparkType));
    setInviteUrl('');
    setInviteExpiresAt('');
    setInviteError('');
    setInviteFilterMode('standard');
  }, [sparkType, activeTab]);

  async function fetchMyInvites() {
    setMyInvitesLoading(true);
    setMyInvitesError('');
    try {
      const res = await apiClient.get('/arcade/invite/mine');
      setMyInvites(res.data?.items || []);
    } catch (e) {
      setMyInvitesError('Could not load your invites.');
      setMyInvites([]);
    } finally {
      setMyInvitesLoading(false);
    }
  }

  async function fetchArcadeHistory() {
    try {
      const res = await apiClient.get('/arcade/history');
      const items = Array.isArray(res.data?.items) ? res.data.items : [];
      if (items.length) {
        const mapped = items.map((it) => ({
          id: it.id || `srv_${it.created_at || Date.now()}`,
          promptId: it.prompt_id || it.promptId || '',
          type: it.type || 'spark',
          prompt: it.prompt || it.prompt_text || '',
          answer: it.answer || '',
          createdAt: it.created_at || it.createdAt || new Date().toISOString(),
        }));
        setHistory(mapped);
        try {
          // cache locally for fast loads
          const { saveSparkHistory } = await import('../lib/arcade/storage');
          saveSparkHistory(mapped);
        } catch {}
      }
    } catch {
      // fallback: keep localStorage history
    }
  }

  function refreshPrompt() {
    setSubmitted(false);
    setAnswer('');
    setCurrentPrompt(sparkType === 'daily' ? getDailyDrop() : getRandomSparkPrompt(sparkType));
    setInviteUrl('');
    setInviteExpiresAt('');
    setInviteError('');
    setInviteFilterMode('standard');
  }

  function submitSoloSpark() {
    if (!currentPrompt) return;
    const trimmed = answer.trim();
    if (!trimmed) return;

    const item = {
      id: `solo_${Date.now()}`,
      promptId: currentPrompt.id,
      type: currentPrompt.type,
      prompt: currentPrompt.text,
      answer: trimmed,
      createdAt: new Date().toISOString(),
    };

    const next = addSparkHistoryItem(item);
    setHistory(next);
    setSubmitted(true);

    // Sync to backend (best-effort). Keeps history across devices.
    apiClient.post('/arcade/history', {
      prompt_id: currentPrompt.id,
      prompt_text: currentPrompt.text,
      answer: trimmed,
      type: currentPrompt.type,
      category: sparkType,
    }).catch(() => {});
  }

  async function createInviteLink() {
    if (!currentPrompt) return;
    const trimmed = answer.trim();
    if (!trimmed) return;

    setInviteLoading(true);
    setInviteError('');
    setInviteUrl('');
    setInviteExpiresAt('');

    // Ensure it's saved to history before we generate a link (helps support/debug)
    if (!submitted) {
      submitSoloSpark();
    }

    try {
      const res = await apiClient.post('/arcade/invite/create', {
        prompt_id: currentPrompt.id,
        prompt_text: currentPrompt.text,
        initiator_answer: trimmed,
        ttl_hours: 72,
        filter_mode: inviteFilterMode,
      });

      setInviteUrl(res.data?.invite_url || '');
      setInviteExpiresAt(res.data?.expires_at || '');
      // Refresh sender inbox so they can see the new invite immediately.
      fetchMyInvites();
    } catch (e) {
      setInviteError('Could not create invite link. Please try again.');
    } finally {
      setInviteLoading(false);
    }
  }

  async function copyInvite() {
    if (!inviteUrl) return;
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setInviteCopied(true);
      setTimeout(() => setInviteCopied(false), 1200);
    } catch {
      // ignore
    }
  }

  function statusPill(status) {
    const base = "inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border";
    switch (status) {
      case "answered":
        return <span className={`${base} border-rose-200 dark:border-rose-900/40 text-rose-700 dark:text-rose-200 bg-rose-50 dark:bg-rose-900/20`}>Answered</span>;
      case "ready":
        return <span className={`${base} border-violet-200 dark:border-violet-900/40 text-violet-700 dark:text-violet-200 bg-violet-50 dark:bg-violet-900/20`}>Ready</span>;
      case "matched":
        return <span className={`${base} border-emerald-200 dark:border-emerald-900/40 text-emerald-700 dark:text-emerald-200 bg-emerald-50 dark:bg-emerald-900/20`}>Matched</span>;
      case "expired":
        return <span className={`${base} border-slate-200 dark:border-slate-800 text-slate-600 dark:text-slate-300 bg-white/60 dark:bg-slate-900/20`}>Expired</span>;
      default:
        return <span className={`${base} border-slate-200 dark:border-slate-800 text-slate-600 dark:text-slate-300 bg-white/60 dark:bg-slate-900/20`}>Waiting</span>;
    }
  }

  const invitePriority = (status) => {
    switch (status) {
      case "ready": return 0;
      case "answered": return 1;
      case "waiting": return 2;
      case "matched": return 3;
      case "expired": return 4;
      default: return 9;
    }
  };

  const inviteTimestamp = (inv) => {
    const t = inv.updated_at || inv.answered_at || inv.created_at || inv.createdAt || inv.created || null;
    const ms = t ? Date.parse(t) : NaN;
    return Number.isFinite(ms) ? ms : 0;
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-950 ul-cosmic">
      <GlobalNav />

      <main className="max-w-5xl mx-auto px-4 pt-24 pb-10">
        <div className="ul-cosmic-bg p-5 sm:p-7">
          <div className="ul-cosmic-stars" />
          <div className="ul-cosmic-arc" />
          <div className="relative">

        {/* Header */}
        <header className="mb-6">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-2xl ul-cosmic-panel flex items-center justify-center">
                <Sparkles className="w-5 h-5" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-extrabold ul-cosmic-title text-white">
                  Connection Arcade
                </h1>
                <p className="mt-0.5 text-sm ul-cosmic-muted">
                  Play with intention. Build real chemistry.
                </p>
              </div>
            </div>
            <div className="hidden sm:block ul-cosmic-join text-sm">JoinUnityLove.com</div>
          </div>

          {/* Hybrid "duel" framing */}
          <div className="mt-4 ul-cosmic-panel p-4 sm:p-5">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-2xl bg-white/10 flex items-center justify-center flex-shrink-0">
                <Swords className="w-5 h-5 text-white/80" />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-extrabold text-white/90">
                  Spark Challenge
                </div>
                <div className="mt-0.5 text-sm ul-cosmic-muted">
                  One question. Two minds. Answer first. Reveal together.
                </div>
                <button
                  onClick={() => setShowHowItWorks(true)}
                  className="mt-2 inline-flex items-center gap-2 text-sm font-extrabold text-white/90 hover:brightness-110"
                >
                  <Info className="w-4 h-4" />
                  How it works
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-6">
          {TABS.map(({ key, label, icon: Icon }) => {
            const isActive = key === activeTab;
            return (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                className={
                  "inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-extrabold transition " +
                  (isActive
                    ? "ul-cosmic-btn"
                    : "ul-cosmic-pill text-white/90 hover:brightness-110")
                }
                aria-current={isActive ? 'page' : undefined}
              >
                <Icon className="w-4 h-4" />
                {label}
              </button>
            );
          })}
        </div>

        {/* Content shell */}
        <section className="ul-cosmic-card p-5 sm:p-6">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-2xl bg-white/10 flex items-center justify-center flex-shrink-0">
              <ActiveIcon className="w-5 h-5 text-white/80" />
            </div>
            <div className="min-w-0">
              <h2 className="text-lg font-extrabold text-white/90">
                {activeTab === 'spark' ? 'Spark' : activeTab === 'chemistry' ? 'Chemistry Lab' : activeTab === 'voice' ? 'Voice Roulette' : 'Micro-Date Builder'}
              </h2>
              <p className="mt-1 text-sm ul-cosmic-muted">
                {activeTab === 'spark'
                  ? 'Short prompts that shift the energy \u2014 playful, real, and low pressure.'
                  : activeTab === 'chemistry'
                    ? 'Quick alignment checks that feel like a game, not an interrogation.'
                    : activeTab === 'voice'
                      ? 'Tiny voice prompts that make connection feel human again.'
                      : 'Spin a micro-date idea and turn momentum into real-world action.'}
              </p>
            </div>
          </div>

          {/* Step 2: Spark Solo */}
          {activeTab === 'spark' ? (
            <div className="mt-6 space-y-5">
              {/* Controls */}
              <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setSparkType('any')}
                    className={
                      "px-4 py-2 rounded-full text-sm font-extrabold transition " +
                      (sparkType === 'any'
                        ? "ul-cosmic-btn"
                        : "ul-cosmic-pill text-white/90 hover:brightness-110")
                    }
                  >
                    Any
                  </button>
                  <button
                    onClick={() => setSparkType('signal')}
                    className={
                      "px-4 py-2 rounded-full text-sm font-extrabold transition " +
                      (sparkType === 'signal'
                        ? "ul-cosmic-btn"
                        : "ul-cosmic-pill text-white/90 hover:brightness-110")
                    }
                    title={SPARK_CATEGORIES.signal.blurb}
                  >
                    Signal
                  </button>
                  <button
                    onClick={() => setSparkType('spark')}
                    className={
                      "px-4 py-2 rounded-full text-sm font-extrabold transition " +
                      (sparkType === 'spark'
                        ? "ul-cosmic-btn"
                        : "ul-cosmic-pill text-white/90 hover:brightness-110")
                    }
                    title={SPARK_CATEGORIES.spark.blurb}
                  >
                    Spark
                  </button>
                  <button
                    onClick={() => setSparkType('daily')}
                    className={
                      "px-4 py-2 rounded-full text-sm font-extrabold transition " +
                      (sparkType === 'daily'
                        ? "ul-cosmic-btn"
                        : "ul-cosmic-pill text-white/90 hover:brightness-110")
                    }
                    title={SPARK_CATEGORIES.daily.blurb}
                  >
                    Daily
                  </button>
                  <button
                    onClick={() => setSparkType('comedy')}
                    className={
                      "px-4 py-2 rounded-full text-sm font-extrabold transition " +
                      (sparkType === 'comedy'
                        ? "ul-cosmic-btn"
                        : "ul-cosmic-pill text-white/90 hover:brightness-110")
                    }
                    title={SPARK_CATEGORIES.comedy.blurb}
                    data-testid="spark-filter-comedy"
                  >
                    Comedy
                  </button>
                  <button
                    onClick={() => setSparkType('expansive')}
                    className={[
                      "relative inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-extrabold transition",
                      sparkType === 'expansive'
                        ? "ul-cosmic-btn"
                        : "ul-cosmic-pill text-white/90 hover:brightness-110",
                      isExpansiveAligned
                        ? "ring-1 ring-violet-300/60 dark:ring-violet-500/40"
                        : "",
                    ].join(" ")}
                    title={isExpansiveAligned ? "Aligned with your relationship style" : SPARK_CATEGORIES.expansive.blurb}
                    data-testid="spark-filter-constellation"
                    type="button"
                  >
                    <span>Constellation</span>
                    {isExpansiveAligned ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-extrabold bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-200">
                        Aligned
                      </span>
                    ) : null}
                  </button>
                </div>

                <div className="sm:ml-auto">
                  <button
                    onClick={refreshPrompt}
                    className="inline-flex items-center gap-2 px-4 py-2 ul-cosmic-pill text-sm font-extrabold text-white/90 hover:brightness-110"
                    title="New prompt"
                  >
                    <RefreshCw className="w-4 h-4" />
                    New prompt
                  </button>
                </div>
              </div>

              {/* Step 6: Sender Inbox (collapsible) */}
              <details
                className="group ul-cosmic-panel p-4 sm:p-5"
              >
                <summary className="list-none cursor-pointer select-none">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-sm font-extrabold text-white/90">
                        My Spark Invites
                        {Array.isArray(myInvites) && myInvites.length > 0 ? (
                          <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-extrabold bg-white/10 text-white/90">
                            {myInvites.length}
                          </span>
                        ) : null}
                      </div>
                      <div className="mt-0.5 text-sm ul-cosmic-muted">
                        See who answered — tap to reveal, then continue into the normal flow.
                      </div>
                    </div>

                    <div className="shrink-0 text-white/70 group-open:rotate-180 transition-transform duration-200">
                      <ChevronDown className="w-5 h-5" />
                    </div>
                  </div>
                </summary>

                <div className="mt-4">
                  <div className="flex items-center justify-end">
                    <button
                      onClick={fetchMyInvites}
                      className="inline-flex items-center gap-2 px-4 py-2 ul-cosmic-pill text-sm font-extrabold text-white/90 hover:brightness-110"
                      title="Refresh"
                      type="button"
                    >
                      <RefreshCw className="w-4 h-4" />
                      Refresh
                    </button>
                  </div>

                  {myInvitesLoading ? (
                    <div className="mt-4 text-sm ul-cosmic-muted">Loading invites…</div>
                  ) : myInvitesError ? (
                    <div className="mt-4 text-sm text-rose-200">{myInvitesError}</div>
                  ) : myInvites.length === 0 ? (
                    <div className="mt-4 text-sm ul-cosmic-muted">
                      No invites yet. Create one below and share it.
                    </div>
                  ) : (
                    <div className="mt-4 space-y-2">
                      {[...myInvites]
                        .map((inv, idx) => ({ inv, idx }))
                        .sort((a, b) => {
                          const pa = invitePriority(a.inv.status);
                          const pb = invitePriority(b.inv.status);
                          if (pa !== pb) return pa - pb;
                          const ta = inviteTimestamp(a.inv);
                          const tb = inviteTimestamp(b.inv);
                          if (ta !== tb) return tb - ta;
                          return a.idx - b.idx;
                        })
                        .map(({ inv }) => {
                        const token = inv.token;
                        const promptText = inv.prompt_text || "Spark prompt";
                        const status = inv.status || "waiting";
                        const invitePath = `/arcade/invite/${token}`;
                        const fullLink =
                          inviteUrl && inviteUrl.includes(token)
                            ? inviteUrl
                            : `${window.location.origin}${invitePath}`;

                        return (
                          <div
                            key={token}
                            className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-2xl bg-black/[0.18] border border-white/10 p-3"
                          >
                            <div className="min-w-0">
                              <div className="flex items-center gap-2">
                                {statusPill(status)}
                                <div className="text-sm font-extrabold text-white truncate">
                                  {promptText}
                                </div>
                              </div>
                              <div className="mt-1 text-xs text-white/60">
                                {status === "ready"
                                  ? "Both answered — tap to reveal."
                                  : status === "answered"
                                    ? "They answered — open to reveal."
                                    : status === "matched"
                                      ? "Matched — you're in the normal flow."
                                      : status === "expired"
                                        ? "Expired."
                                        : "Waiting for a response."}
                              </div>
                            </div>

                            <div className="flex items-center gap-2">
                              <button
                                onClick={async () => {
                                  try {
                                    await navigator.clipboard.writeText(fullLink);
                                    setInviteCopied(true);
                                    setTimeout(() => setInviteCopied(false), 1200);
                                  } catch {}
                                }}
                                className="inline-flex items-center gap-2 px-4 py-2 ul-cosmic-pill text-sm font-extrabold text-white/90 hover:brightness-110"
                                title="Copy link"
                                type="button"
                              >
                                <Copy className="w-4 h-4" />
                                Copy
                              </button>

                              <button
                                onClick={() => {
                                  try {
                                    localStorage.setItem("arcade_invites_seen_at", String(Date.now()));
                                  } catch (_) {}
                                  window.dispatchEvent(new Event("arcade_invites_seen"));
                                  window.location.assign(invitePath);
                                }}
                                className="inline-flex items-center gap-2 px-4 py-2 ul-cosmic-btn text-sm"
                                title="Open invite"
                                type="button"
                              >
                                <ExternalLink className="w-4 h-4" />
                                View
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </details>

              {/* Prompt card */}
              <div className="ul-cosmic-card p-4 sm:p-5">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-extrabold tracking-wide text-white/70">
                      SOLO PLAY
                    </span>
                    {currentPrompt?.type ? (
                      <span className="text-xs font-extrabold px-2 py-0.5 rounded-full bg-white/10 text-white/90">
                        {SPARK_CATEGORIES[currentPrompt.type]?.label || currentPrompt.type}
                      </span>
                    ) : null}
                  </div>
                  <div className="text-xs font-extrabold text-white/60">
                    {submitted ? 'Reveal phase' : 'Round 1 of 1'} <span className="ml-2">{submitted ? '\u25CF\u25CF' : '\u25CF\u25CB'}</span>
                  </div>
                </div>

                <div className="mt-3 text-2xl sm:text-3xl font-extrabold text-white leading-tight">
                  {currentPrompt?.text || 'Loading prompt\u2026'}
                </div>

                {/* Duel arena */}
                <div className="mt-5 grid grid-cols-1 gap-3">
                  {/* Locked "Their Answer" */}
                  <div className="rounded-2xl bg-black/[0.18] border border-white/10 p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Lock className="w-4 h-4 text-white/60" />
                        <div className="text-sm font-extrabold text-white">
                          Their Answer
                        </div>
                      </div>
                      <div className="text-xs font-extrabold text-white/60">
                        Locked
                      </div>
                    </div>
                    <div className="mt-2 text-sm ul-cosmic-muted">
                      Waiting for your move…
                    </div>
                  </div>

                  {/* VS divider */}
                  <div className="relative py-1">
                    <div className="h-px bg-white/10" />
                    <div className="absolute left-1/2 -translate-x-1/2 -top-3">
                      <div className="px-2 py-1 rounded-full text-xs font-black tracking-wide bg-black/30 border border-white/10 text-white/80">
                        VS
                      </div>
                    </div>
                  </div>

                  {/* Your Move */}
                  <div className="rounded-2xl bg-black/[0.18] border border-white/10 p-4">
                    <div className="text-xs font-extrabold tracking-wide text-white/65 uppercase">
                      Your move
                    </div>
                    <div className="mt-2">
                      <textarea
                        value={answer}
                        onChange={(e) => setAnswer(e.target.value)}
                        rows={4}
                        placeholder="Type your answer\u2026"
                        className="w-full rounded-2xl border border-white/10 bg-black/20 p-3 text-white placeholder:text-white/45 focus:outline-none focus:ring-2 focus:ring-fuchsia-300/30"
                      />
                    </div>
                  </div>
                </div>

                <div className="mt-4 flex flex-col sm:flex-row sm:items-center gap-3">
                  <button
                    onClick={submitSoloSpark}
                    disabled={!answer.trim() || !currentPrompt}
                    className={
                      "inline-flex items-center justify-center gap-2 w-full sm:w-auto ul-cosmic-btn " +
                      (!answer.trim() || !currentPrompt
                        ? "opacity-50 cursor-not-allowed"
                        : "")
                    }
                  >
                    <CheckCircle2 className="w-4 h-4" />
                    {submitted ? 'Saved' : 'Submit'}
                  </button>

                  {submitted ? (
                    <div className="text-sm text-white/80">
                      Saved. Ready to share or create a match.
                      <div className="mt-2 flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            setShareError('');
                            setShareInviteToken('');
                            setShareInviteUrl('');
                            setShareIncludeAnswer(false);
                            setShareOpen(true);
                          }}
                          className="inline-flex items-center gap-2 px-4 py-2 ul-cosmic-btn text-sm"
                          data-testid="share-card-btn"
                          title="Create a shareable story card"
                        >
                          Share Card
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="text-sm text-white/55">
                      Your solo answers don&apos;t affect matching (for now).
                    </div>
                  )}
                </div>
              </div>

              {/* History (collapsible) */}
              <details className="group ul-cosmic-panel p-4 sm:p-5">
                <summary className="list-none cursor-pointer select-none">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-sm font-extrabold text-white/90">
                        Your Spark History
                        {Array.isArray(history) && history.length > 0 ? (
                          <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-extrabold bg-white/10 text-white/90">
                            {history.length}
                          </span>
                        ) : null}
                      </div>
                      <div className="mt-0.5 text-xs text-white/60">
                        Most recent first (max 50)
                      </div>
                    </div>

                    <div className="shrink-0 text-white/70 group-open:rotate-180 transition-transform duration-200">
                      <ChevronDown className="w-5 h-5" />
                    </div>
                  </div>
                </summary>

                <div className="mt-4">
                  {history.length === 0 ? (
                    <div className="text-sm ul-cosmic-muted">
                      No entries yet. Submit your first Spark above.
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {history.slice(0, 10).map((h) => (
                        <div
                          key={h.id}
                          className="rounded-2xl bg-black/[0.18] border border-white/10 p-3"
                        >
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-extrabold px-2 py-0.5 rounded-full bg-white/10 text-white/90">
                              {SPARK_CATEGORIES[h.type]?.label || h.type}
                            </span>
                            <span className="text-xs text-white/60">
                              {new Date(h.createdAt).toLocaleString()}
                            </span>
                          </div>
                          <div className="mt-2 text-sm font-extrabold text-white">{h.prompt}</div>
                          <div className="mt-1 text-sm text-white/80 whitespace-pre-wrap">
                            {h.answer}
                          </div>
                        </div>
                      ))}
                      {history.length > 10 ? (
                        <div className="text-xs text-white/60">
                          Showing 10 of {history.length}. (Full history view can come later.)
                        </div>
                      ) : null}
                    </div>
                  )}
                </div>
              </details>

              {/* Step 3: Invite link generator */}
              <div className="ul-cosmic-card p-4 sm:p-5">
                <div className="text-xs font-extrabold tracking-wide text-white/65">
                  INVITE (PHASE 2)
                </div>
                <div className="mt-2 text-sm font-extrabold text-white/90">
                  Share this Spark
                </div>
                <p className="mt-1 text-sm ul-cosmic-muted">
                  Create a link that lets someone answer this prompt first - then they funnel into UnityLOVE.
                </p>

                <div className="mt-3 flex flex-col sm:flex-row sm:items-center gap-2">
                  <div className="flex items-center gap-2">
                    <label className="text-xs font-extrabold text-white/65">Arcade range</label>
                    <select
                      value={inviteFilterMode}
                      onChange={(e) => setInviteFilterMode(e.target.value)}
                      className="text-sm rounded-xl border border-white/10 bg-black/20 px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-fuchsia-300/30"
                      title="Standard uses your normal Discover filters. Expanded adds slightly more variety (soft widen only)."
                    >
                      <option value="standard">Standard</option>
                      <option value="expanded">Expanded</option>
                    </select>
                  </div>

                  <button
                    onClick={createInviteLink}
                    disabled={!answer.trim() || !currentPrompt || inviteLoading}
                    className={
                      "inline-flex items-center justify-center gap-2 ul-cosmic-btn w-full sm:w-auto " +
                      (!answer.trim() || !currentPrompt || inviteLoading
                        ? "opacity-50 cursor-not-allowed"
                        : "")
                    }
                  >
                    {inviteLoading ? 'Creating...' : 'Create invite link'}
                  </button>

                  {inviteUrl ? (
                    <div className="flex-1 flex flex-col sm:flex-row sm:items-center gap-2">
                      <div className="flex-1 rounded-xl border border-white/10 bg-black/20 px-3 py-2 text-xs text-white break-all">
                        {inviteUrl}
                      </div>
                      <button
                        onClick={copyInvite}
                        className="inline-flex items-center justify-center gap-2 px-4 py-2 ul-cosmic-pill text-sm font-extrabold text-white/90 hover:brightness-110"
                        title="Copy link"
                      >
                        <Copy className="w-4 h-4" />
                        {inviteCopied ? 'Copied' : 'Copy'}
                      </button>
                      <a
                        href={inviteUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center justify-center gap-2 px-4 py-2 ul-cosmic-pill text-sm font-extrabold text-white/90 hover:brightness-110"
                        title="Open invite"
                      >
                        <ExternalLink className="w-4 h-4" />
                        Open
                      </a>
                    </div>
                  ) : null}
                </div>

                {inviteExpiresAt ? (
                  <div className="mt-2 text-xs text-white/60">
                    Expires: {new Date(inviteExpiresAt).toLocaleString()}
                  </div>
                ) : null}

                {inviteError ? (
                  <div className="mt-2 text-sm text-rose-200">{inviteError}</div>
                ) : null}
              </div>
            </div>
          ) : (
            /* Other tabs remain placeholder for now */
            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="rounded-2xl bg-black/[0.18] border border-white/10 p-4">
                <div className="text-xs font-extrabold tracking-wide text-white/65">
                  COMING SOON
                </div>
                <div className="mt-2 text-sm font-extrabold text-white/90">
                  {activeTab === 'chemistry' ? 'Chemistry Lab' : activeTab === 'voice' ? 'Voice Roulette' : 'Micro-Date Builder'}
                </div>
                <p className="mt-1 text-sm ul-cosmic-muted">
                  This tab will light up after Spark Solo is stable and verified.
                </p>
              </div>
            </div>
          )}
        </section>

        {/* Share Card modal */}
        {shareOpen ? (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div
              className="absolute inset-0 bg-black/60"
              onClick={() => (shareBusy ? null : setShareOpen(false))}
            />
            <div className="relative w-full max-w-md rounded-2xl border border-white/10 ul-cosmic-card p-4 sm:p-5 shadow-xl">
              <div className="text-base font-extrabold text-white">
                Share your Spark
              </div>
              <div className="mt-1 text-sm ul-cosmic-muted">
                Prompt is always included. Your answer is optional.
              </div>

              <div className="mt-4 rounded-2xl bg-black/[0.18] border border-white/10 p-3">
                <div className="text-xs font-bold tracking-widest text-white/65">
                  {currentCategoryLabel.toUpperCase()}
                </div>
                <div className="mt-2 text-sm font-extrabold text-white">
                  {currentPrompt?.text || ''}
                </div>
              </div>

              <div className="mt-4 rounded-2xl bg-black/[0.18] border border-white/10 p-3">
                <div className="text-xs font-bold tracking-widest text-white/65">
                  SHARE LINK
                </div>
                <div className="mt-1 text-xs ul-cosmic-muted">
                  This creates an invite so someone can answer the same Spark and unlock the reveal.
                </div>

                <div className="mt-3 flex items-center gap-2">
                  <input
                    value={shareInviteUrl}
                    readOnly
                    placeholder="Generated when you Share / Download\u2026"
                    className="w-full rounded-xl border border-white/10 bg-black/20 px-3 py-2 text-xs text-white"
                    data-testid="share-invite-url-input"
                  />
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        const { invite_url } = await ensureShareInviteUrl();
                        await navigator.clipboard.writeText(invite_url);
                      } catch {}
                    }}
                    className="shrink-0 inline-flex items-center gap-2 px-3 py-2 ul-cosmic-pill text-xs font-extrabold text-white/90 hover:brightness-110"
                    disabled={shareBusy}
                    title="Copy invite link"
                    data-testid="share-copy-link-btn"
                  >
                    Copy
                  </button>
                </div>
              </div>

              <label className="mt-4 flex items-start gap-3 cursor-pointer select-none">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={shareIncludeAnswer}
                  onChange={(e) => setShareIncludeAnswer(e.target.checked)}
                  disabled={shareBusy}
                  data-testid="share-include-answer-checkbox"
                />
                <div>
                  <div className="text-sm font-extrabold text-white">
                    Include my answer
                  </div>
                  <div className="text-xs ul-cosmic-muted">
                    Only share what you're comfortable with.
                  </div>
                </div>
              </label>

              {shareError ? (
                <div className="mt-3 text-sm text-rose-200">
                  {shareError}
                </div>
              ) : null}

              <div className="mt-5 flex gap-2">
                <button
                  type="button"
                  onClick={() => setShareOpen(false)}
                  className="flex-1 rounded-2xl ul-cosmic-pill px-4 py-3 text-sm font-semibold text-white/90 hover:brightness-110"
                  disabled={shareBusy}
                  data-testid="share-cancel-btn"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleShareCard}
                  className="flex-1 rounded-2xl ul-cosmic-btn px-4 py-3 text-sm"
                  disabled={shareBusy}
                  data-testid="share-download-btn"
                >
                  {shareBusy ? 'Generating\u2026' : 'Share / Download'}
                </button>
              </div>
            </div>
          </div>
        ) : null}

        {/* How it works modal */}
        {showHowItWorks ? (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/40" onClick={() => setShowHowItWorks(false)} />
            <div className="relative w-full max-w-md rounded-2xl border border-white/10 ul-cosmic-card shadow-xl p-5">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-lg font-extrabold text-white">How Spark Works</div>
                  <div className="mt-1 text-sm ul-cosmic-muted">
                    A fast, low-pressure exchange that unlocks together.
                  </div>
                </div>
                <button
                  onClick={() => setShowHowItWorks(false)}
                  className="p-2 rounded-xl hover:brightness-110 text-white/70"
                  aria-label="Close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="mt-4 space-y-3">
                <div className="rounded-2xl bg-black/[0.18] border border-white/10 p-3">
                  <div className="text-sm font-extrabold text-white">1) Your move</div>
                  <div className="mt-1 text-sm ul-cosmic-muted">
                    Answer the prompt — short and real.
                  </div>
                </div>
                <div className="rounded-2xl bg-black/[0.18] border border-white/10 p-3">
                  <div className="text-sm font-extrabold text-white">2) Invite</div>
                  <div className="mt-1 text-sm ul-cosmic-muted">
                    Share the link. They answer without needing a match first.
                  </div>
                </div>
                <div className="rounded-2xl bg-black/[0.18] border border-white/10 p-3">
                  <div className="text-sm font-extrabold text-white">3) Reveal + Continue</div>
                  <div className="mt-1 text-sm ul-cosmic-muted">
                    Both answers unlock, then you continue into the normal UnityLOVE flow.
                  </div>
                </div>
              </div>

              <button
                onClick={() => setShowHowItWorks(false)}
                className="mt-5 w-full ul-cosmic-btn"
              >
                Got it
              </button>
            </div>
          </div>
        ) : null}

        <div className="mt-10">
          <GlobalFooter />
        </div>

          </div>
        </div>
      </main>
    </div>
  );
}
